/**
 * createListHook — composition factory for codegen-emitted list hooks.
 *
 * Returns a hook whose result extends the base `useQuery` result with:
 *
 *   - `itemObserver(id)` — stable per-id ref callback for viewport tracking.
 *   - `Items`            — render-prop component that owns iteration AND
 *                          per-row subscription INTERNALLY. This is the
 *                          structural fix for the "every card re-renders
 *                          when one row changes" cascade: the consumer reads
 *                          ONLY `Items` / `isPending` / `isError` and never
 *                          `data`, so TanStack's tracked-properties Proxy
 *                          never notifies the parent on row patches. Each
 *                          row is subscribed in isolation inside `Items`.
 *
 * Usage from consumer code (one call site shape across the whole SDK):
 *
 *   const { Items, isPending, isError } = useGetMarkets({ fields, viewportGated: true });
 *
 *   return (
 *     <Items>
 *       {({ row, ref }) => <Card data={row} ref={ref} />}
 *     </Items>
 *   );
 *
 * Engineers never construct list hooks directly — codegen emits one
 * `createListHook(...)` call per realtime list-kind query in the manifest.
 */
import { hashKey, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getSdkAuthStore } from '../../auth/state/auth-state.js';
import { registerRealtimeSpec } from '../../cache/persist/realtime-query-registry.js';
import { _bumpPerf } from '../../devtool/perf-counters.js';
import { projectLiveSinceMountIds, readFenceFromResult } from '../../nats/live-since-mount-store.js';
import { hashDiagnosticEntity } from '../../tx-engine/diagnostics/lifecycle.js';
import { materializeNestedEntityProjection, projectEntityField, projectEntityRow, recordOutcomeEntityDiagnostic } from '../internal/entity-projection.js';
import { recordParticipationEntityDiagnostic } from '../internal/participation-entity.js';
// Stable module-level reference. Per docs §select.memoization, an inlined
// select runs on every render — keeping this as a single shared constant
// reference means the page-level observer's select identity never changes,
// so the function only re-runs when `data` actually changes (which it then
// trivially collapses to the same `null` result → no notify).
const PAGE_LEVEL_SELECT = () => null;
const PAGE_SCOPE_SELECT = data => ({
  responseSubjectIds: data.responseSubjectIds
});
import { useSdkNetworkScope, useSdkViewerScope } from '../../provider/SdkRuntimeContext.js';
import { createItemsComponent } from '../components/Items.js';
import { emit } from '../events.js';
import { useItemObserver } from '../hooks/useItemObserver.js';
import { materializeActivityEntityRow } from '../internal/activity-entity.js';
import { appendNormalizedPage, readEntityRows, splitRowsIntoEntities } from '../internal/normalized-entity.js';
import { refetchRealtimeQuery } from '../internal/refetchRealtimeQuery.js';
import { recordBackerRewardDiagnostic, resolveEntityQueryKey } from '../internal/scoped-entity-store.js';
import { filterTerminalRows } from '../internal/terminal-row-fence.js';
import { useRetainNormalizedEntityReferences } from '../internal/use-retain-normalized-entity-references.js';
import { useListIds } from '../internal/useListIds.js';
import { useTrackedQueryValue } from '../internal/useTrackedQueryValue.js';
import { canMaterializeViewerRelationshipCollection, materializeViewerRelationshipData, useViewerRelationshipIndexIds, viewerRelationshipIndexQueryKey, viewerRelationshipScope, writeViewerRelationshipCollection } from '../internal/viewer-relationship.js';
import { createHook } from './createHook.js';
const EMPTY_LIVE_IDS = Object.freeze([]);
const EMPTY_ROWS = Object.freeze([]);
const EMPTY_ENTITY_QUERY_KEY = () => [];
export function createListHook(opts) {
  // Page-level observers use `select: () => null` (docs §Render
  // Optimizations · select). The consumer page never reads `data` from a
  // list hook — iteration happens via `<Items>` and per-field hooks, each
  // with their OWN select observers on the same cache entry. Collapsing the
  // page's select to a constant means structural sharing keeps the selected
  // value identity-stable across patches.
  // Docs: https://tanstack.com/query/latest/docs/framework/react/guides/render-optimizations
  // The row-identifier key comes from the realtime spec. Codegen-emitted
  // specs set this as a literal `as const` (e.g. `rowKey: 'pubkey'`).
  // Derived BEFORE `createHook` so it can be threaded into the
  // pagination-aware refetch wrapper (dedup key when restoring pages).
  // Persist-exclusion: a realtime query must never be written to the
  // persisted cache (it rehydrates from the live wire). Register its
  // key-prefix eagerly at module load so the flush excludes even
  // SSR-prefetched, never-observed instances (which reach the cache with
  // no `meta`). No-op for non-realtime specs.
  registerRealtimeSpec(opts.spec);
  const postWireOnce = opts.snapshotMode === 'post-wire-once';
  const rowKey = opts.realtime.rowKey;
  if (typeof rowKey !== 'string' || rowKey.length === 0) {
    throw new Error(`createListHook(${opts.spec.specName}): realtime.rowKey must be a non-empty string`);
  }
  const _hook = createHook(opts.spec, opts.realtime, {
    // Chart owners need the complete envelope, including sparse-series
    // metadata, on every NATS patch. Other lists keep page-level isolation.
    ...(postWireOnce ? {} : {
      select: opts.realtime.responseSubjects === undefined ? PAGE_LEVEL_SELECT : PAGE_SCOPE_SELECT
    }),
    ...(opts.spec.viewerRelationship?.collection === undefined && opts.pagination === undefined ? {} : {
      responseBoundary: (data, context) => {
        const relationship = opts.spec.viewerRelationship;
        if (relationship?.collection !== undefined) {
          writeViewerRelationshipCollection(context.queryClient, context.queryKey, viewerRelationshipScope(context.viewerScope, context.networkScope), relationship, context.args, data, context.viewerAccountId, {
            source: 'rest',
            observedAtMs: context.observedAtMs
          });
        }
        return opts.pagination === undefined ? data : {
          ...data,
          _restPageLastTimestamp: lastPageTimestamp(data.data)
        };
      }
    }),
    // A parent refetch must restore every loaded page, not just
    // page 1 — otherwise reconnect / reacquire / pull-to-refresh
    // truncates a scrolled list to `pageSize` rows and blanks the
    // viewport. See `CreateHookExtras.paginatedRefetch`.
    ...(opts.pagination !== undefined ? {
      paginatedRefetch: {
        fetchPage: opts.pagination.fetchPage,
        maxPageSize: opts.pagination.maxPageSize,
        pageSize: opts.pagination.pageSize,
        limitParam: opts.pagination.limitParam,
        rowKey
      }
    } : {})
  });
  return function useList(args, callOpts) {
    // `viewportGated: true` on a list with an entity key activates PER-ITEM
    // gating via `itemObserver` (wired through `Items`). The legacy container-
    // level gate (`acquireListVisibility`) would otherwise block dispatch
    // until a ref is attached to the list container — which consumers no
    // longer do. Strip the flag at this seam so the underlying `createHook`
    // doesn't open the container gate.
    const innerArgs = opts.entityKey !== null && args.viewportGated === true ? {
      ...args,
      viewportGated: false
    } : args;
    // `opts.enabled === false` short-circuits REST and realtime acquisitions
    // (the inner `createHook` mirrors TanStack's `enabled` semantics). Used
    // to gate a list call on a runtime condition — e.g. an anonymous viewer
    // whose empty `userId` would otherwise build a malformed
    // `/v1/users//gifts` request.
    const q = _hook(innerArgs, postWireOnce ? {
      ...callOpts,
      eagerSnapshot: false,
      fetchOnce: true,
      singleSnapshot: true
    } : callOpts);
    // Per-row subjects come from the hook's own realtime contract, not
    // from the entity-key fan-out in `nats.manifest.yml#channels`.
    // Subscribing to channels the hook never declared in its `realtime:`
    // block would over-subscribe the wire and mislead the devtool
    // surface about what events this hook consumes.
    //
    // `rowSubjectTemplates` (DEF-10) — NOT `perEntitySubjects`. The
    // latter is the DOMAIN-wildcard form (`morfi.market.*.user.*.…`)
    // for trie dispatch; substituting a row id into it would clobber
    // the literal `*` actor segment too. `rowSubjectTemplates` keeps
    // the row dimension as a `{rowKey}` placeholder and the actor `*`
    // literal, so `concretize` binds ONLY the row and the resulting
    // bounded subject reaches `filter_subjects`. Falls back to
    // `perEntitySubjects` only for specs predating the codegen field
    // (none in tree — kept defensive).
    const realtimeRT = opts.realtime;
    const perEntityTemplates = realtimeRT.rowSubjectTemplates?.(args) ?? realtimeRT.perEntitySubjects?.(args) ?? [];
    // Pull the queryKey from the spec factory so `Items` can target the
    // exact cache entry. The factory is deterministic over `args` — same
    // args → same key — but allocates a fresh array each call.
    const factory = opts.spec.entityKeys.list;
    const recoveryQueryOptions = factory?.(args);
    const queryKey = recoveryQueryOptions?.queryKey ?? [];
    const queryHash = hashKey(queryKey);
    const resolvedRowsRef = useRef(null);
    const queryClient = useQueryClient();
    const viewerScope = useSdkViewerScope();
    const networkScope = useSdkNetworkScope();
    const relationshipScope = viewerRelationshipScope(viewerScope, networkScope);
    const viewerArgs = args;
    const viewerAccountId = getSdkAuthStore().current().user?.id ?? null;
    const relationship = opts.spec.viewerRelationship;
    const relationshipIndexKey = relationship?.collection === undefined || !canMaterializeViewerRelationshipCollection(relationship, viewerArgs, viewerAccountId) ? undefined : viewerRelationshipIndexQueryKey(relationshipScope, relationship.kind, queryKey);
    const relationshipIds = useViewerRelationshipIndexIds(queryClient, relationshipIndexKey);
    const relationshipSourceRef = useRef(queryKey);
    relationshipSourceRef.current = queryKey;
    const relationshipArgsRef = useRef(viewerArgs);
    relationshipArgsRef.current = viewerArgs;
    useEffect(() => {
      void queryHash;
      if (relationship?.collection === undefined || relationshipScope.length === 0) return;
      const sourceQueryKey = relationshipSourceRef.current;
      const data = queryClient.getQueryData(sourceQueryKey);
      if (data === undefined) return;
      writeViewerRelationshipCollection(queryClient, sourceQueryKey, relationshipScope, relationship, relationshipArgsRef.current, data, viewerAccountId, {
        source: 'hydrate',
        observedAtMs: 0
      });
    }, [queryClient, queryHash, relationship, relationshipScope, viewerAccountId]);
    const entityQueryKey = resolveEntityQueryKey(opts.spec, args, viewerScope);
    const isNormalized = opts.spec.normalized === true && entityQueryKey !== undefined;
    const entityRowProjector = opts.spec.entityProjection === undefined && opts.spec.nestedEntityProjection === undefined ? undefined : row => {
      const projected = projectEntityRow(row, opts.spec.entityProjection);
      const activity = opts.spec.entityProjection?.resource === 'activity' ? materializeActivityEntityRow(queryClient, projected, networkScope) : projected;
      return materializeNestedEntityProjection(queryClient, activity, opts.spec.nestedEntityProjection);
    };
    // Per-row FIELD resolver for `useItemObserver.concretize`. A `{field}`
    // placeholder in a rowSubjectTemplate (a `rowField:` binding — e.g.
    // positions' `{market}` / `{outcomeMint}`) is filled from the row's OWN
    // field value: look the row up by `rowKey` in the list cache and read the
    // field. The `rowKey` placeholder itself short-circuits to the id (no
    // lookup), so rowKey-bound hooks (markets `{pubkey}`) are unchanged. Reads
    // the latest queryKey via a ref so the callback identity stays stable across
    // arg changes (no observer churn); the lookup runs at attach time, when the
    // row is already in cache.
    const resolveInputsRef = useRef({
      queryKey,
      rowKey
    });
    resolveInputsRef.current = {
      queryKey,
      rowKey
    };
    const resolveField = useCallback((id, field) => {
      const {
        queryKey: qk,
        rowKey: rk
      } = resolveInputsRef.current;
      if (field === rk) return id;
      // Normalized: the row lives at its own entity key, not in the feed's
      // `data` array (which holds only ids). Fall through to the classic
      // scan when the entity is absent (an un-split full-row feed, pre-heal).
      if (isNormalized && entityQueryKey !== undefined) {
        const row = queryClient.getQueryData(entityQueryKey(id));
        if (row !== undefined) {
          const v = projectEntityField(row, field, opts.spec.entityProjection);
          return v === undefined || v === null ? null : String(v);
        }
      }
      const data = queryClient.getQueryData(qk);
      const rowsData = data?.data;
      if (rowsData === undefined) return null;
      for (const row of rowsData) {
        if (String(row[rk]) === id) {
          const v = row[field];
          return v === undefined || v === null ? null : String(v);
        }
      }
      return null;
    }, [queryClient, isNormalized, entityQueryKey]);
    const itemObserver = useItemObserver(perEntityTemplates, resolveField);
    // Cache-attached subscriber observer over the row-id set. Shared
    // implementation with `<Items>` (same hook), so consumers using
    // `<Items>` AND `ids` only pay for ONE id-set observer per queryKey
    // (TanStack collapses identical observer configs). Stable reference
    // via structural sharing — see `useListIds` for the contract.
    const sourceIds = useListIds(queryKey, rowKey);
    const ids = relationshipIds ?? sourceIds;
    useRetainNormalizedEntityReferences(queryClient, entityQueryKey ?? EMPTY_ENTITY_QUERY_KEY, isNormalized ? ids : [], isNormalized ? opts.spec.entityGcTimeMs : undefined);
    // A feed hydrated un-split (SSR dehydration, a direct `setQueryData`)
    // still holds full rows on its first commit; the heal effect below splits
    // them into entities in that same commit, so nothing is missing yet.
    // Treating them as missing would refetch a hydrated feed for nothing.
    const missingEntityIds = isNormalized && entityQueryKey !== undefined && !holdsUnsplitRows(queryClient, queryKey) ? ids.filter(id => queryClient.getQueryData(entityQueryKey(id)) === undefined) : [];
    const missingEntityRecoveryKey = hashKey([queryKey, missingEntityIds]);
    const missingEntityRecoveryRef = useRef(null);
    useEffect(() => {
      if (missingEntityIds.length === 0 || entityQueryKey === undefined) {
        missingEntityRecoveryRef.current = null;
        return;
      }
      if (missingEntityRecoveryRef.current === missingEntityRecoveryKey) return;
      missingEntityRecoveryRef.current = missingEntityRecoveryKey;
      void (async () => {
        const outcome = await refetchRealtimeQuery(queryClient, queryKey);
        let stillMissing = missingEntityIds.some(id => queryClient.getQueryData(entityQueryKey(id)) === undefined);
        try {
          if (stillMissing && recoveryQueryOptions?.queryFn !== undefined) {
            const observedAtMs = Date.now();
            let page = await recoveryQueryOptions.queryFn({
              queryKey,
              signal: new AbortController().signal,
              client: queryClient,
              meta: undefined
            });
            splitRowsIntoEntities(queryClient, page, entityQueryKey, rowKey, {
              source: 'rest',
              observedAtMs
            }, opts.spec.viewerOverlay, viewerArgs, viewerScope, opts.spec.entityProjection, opts.spec.nestedEntityProjection, opts.spec.entityGcTimeMs, queryKey);
            let cursor = page.nextCursor;
            while (cursor !== null && opts.pagination !== undefined && missingEntityIds.some(id => queryClient.getQueryData(entityQueryKey(id)) === undefined)) {
              page = await opts.pagination.fetchPage(args, cursor);
              splitRowsIntoEntities(queryClient, page, entityQueryKey, rowKey, {
                source: 'rest',
                observedAtMs
              }, opts.spec.viewerOverlay, viewerArgs, viewerScope, opts.spec.entityProjection, opts.spec.nestedEntityProjection, opts.spec.entityGcTimeMs, queryKey);
              cursor = page.nextCursor;
            }
            stillMissing = missingEntityIds.some(id => queryClient.getQueryData(entityQueryKey(id)) === undefined);
          }
        } catch {
          stillMissing = true;
        }
        if (opts.spec.entityStore?.resource === 'backer-rewards' && stillMissing) {
          recordBackerRewardDiagnostic({
            kind: 'missing-entity-recovery-failure',
            entityHash: hashDiagnosticEntity(missingEntityIds.join(',')),
            reason: outcome
          });
        }
        if (opts.spec.entityProjection?.resource === 'outcomes' && stillMissing) {
          recordOutcomeEntityDiagnostic({
            kind: 'missing-entity-recovery-failure',
            entityHash: hashDiagnosticEntity(missingEntityIds.join(',')),
            source: opts.spec.specName
          });
        }
        if (opts.spec.entityProjection?.resource === 'participation' && stillMissing) {
          recordParticipationEntityDiagnostic({
            kind: 'rank-index-recovery-failure',
            entityHash: hashDiagnosticEntity(missingEntityIds.join(',')),
            source: opts.spec.specName
          });
        }
        const entityPrefix = entityQueryKey('__diagnostic__');
        if (entityPrefix[0] === 'trades' && entityPrefix[1] === 'entity' && stillMissing) {
          if (process.env.NODE_ENV !== 'production') {
            _bumpPerf('tradeEntity.reconciliationFailures');
          }
          emit('trade-entity:diagnostic', {
            kind: 'missing-entity-recovery-failure',
            entityHash: hashDiagnosticEntity(missingEntityIds.slice().sort().join(',')),
            missingEntityCount: missingEntityIds.length,
            recoveryOutcome: outcome,
            source: opts.spec.specName
          });
        }
      })();
    }, [args, entityQueryKey, missingEntityIds, missingEntityRecoveryKey, queryClient, queryKey, recoveryQueryOptions, viewerArgs, viewerScope]);
    // Prefetch/hydration may write full rows after an inactive list mounted.
    // Normalize at that cache boundary, before nested field readers need them,
    // without making the page observe every live data patch.
    useEffect(() => {
      if (!isNormalized || entityQueryKey === undefined) return;
      const {
        queryKey: qk,
        rowKey: rk
      } = resolveInputsRef.current;
      const heal = () => {
        const current = queryClient.getQueryData(qk);
        const currentRows = current?.data;
        if (!Array.isArray(currentRows) || currentRows.length === 0) return;
        if (typeof currentRows[0] === 'string') return;
        const updatedAt = queryClient.getQueryState(qk)?.dataUpdatedAt;
        const idEnv = splitRowsIntoEntities(queryClient, current, entityQueryKey, rk, undefined, opts.spec.viewerOverlay, viewerArgs, viewerScope, opts.spec.entityProjection, opts.spec.nestedEntityProjection, opts.spec.entityGcTimeMs, qk);
        if (idEnv !== undefined) queryClient.setQueryData(qk, idEnv, updatedAt === undefined ? undefined : {
          updatedAt
        });
      };
      heal();
      return queryClient.getQueryCache().subscribe(event => {
        if (event.type === 'updated' && event.action.type === 'success' && hashKey(event.query.queryKey) === queryHash) {
          heal();
        }
      });
    }, [isNormalized, entityQueryKey, queryClient, queryHash, viewerArgs, viewerScope]);
    const autoLoadMore = opts.autoLoadMore === true;
    // `Items` is built EXACTLY ONCE per hook instance and reads the current
    // queryKey / observer from this live ref every render. A filter/sort
    // switch changes `queryKey` — but `Items` (and the `useListIds` observer
    // inside it) must NOT remount, or the observer loses its previous data
    // and the `keepPreviousData` that holds the prior rows on screen is
    // defeated, flashing an empty grid mid-switch. Re-rendering the SAME
    // `Items` with a new queryKey keeps the observer alive across the swap.
    //
    // The ref is refreshed during render: `Items` renders synchronously as a
    // descendant of this same render pass, so it always reads the current
    // value. `triggerNextPage` is a hoisted declaration whose closure reads
    // `triggerNextPageRef.current`, so the latest `fetchNextPage` always runs.
    const itemsRuntimeRef = useRef({
      queryKey,
      rowKey: rowKey,
      itemObserver,
      onLastRowVisible: autoLoadMore ? triggerNextPage : undefined,
      ...(isNormalized && entityQueryKey !== undefined ? {
        entityQueryKey
      } : {}),
      ...(opts.spec.entityProjection !== undefined ? {
        entityProjection: opts.spec.entityProjection
      } : {}),
      ...(opts.spec.nestedEntityProjection !== undefined ? {
        nestedEntityProjection: opts.spec.nestedEntityProjection
      } : {}),
      ...(opts.spec.viewerOverlay !== undefined ? {
        viewerOverlay: opts.spec.viewerOverlay,
        viewerArgs,
        viewerScope
      } : {}),
      ...(relationshipIds === undefined ? {} : {
        viewerRelationshipIds: relationshipIds
      })
    });
    itemsRuntimeRef.current = {
      queryKey,
      rowKey: rowKey,
      itemObserver,
      onLastRowVisible: autoLoadMore ? triggerNextPage : undefined,
      ...(isNormalized && entityQueryKey !== undefined ? {
        entityQueryKey
      } : {}),
      ...(opts.spec.entityProjection !== undefined ? {
        entityProjection: opts.spec.entityProjection
      } : {}),
      ...(opts.spec.nestedEntityProjection !== undefined ? {
        nestedEntityProjection: opts.spec.nestedEntityProjection
      } : {}),
      ...(opts.spec.viewerOverlay !== undefined ? {
        viewerOverlay: opts.spec.viewerOverlay,
        viewerArgs,
        viewerScope
      } : {}),
      ...(relationshipIds === undefined ? {} : {
        viewerRelationshipIds: relationshipIds
      })
    };
    const itemsRef = useRef(null);
    if (itemsRef.current === null) {
      itemsRef.current = createItemsComponent(itemsRuntimeRef);
    }
    const Items = itemsRef.current;
    const [, setPaginationRevision] = useState(0);
    const inFlightByQueryRef = useRef(new Map());
    const failuresByQueryRef = useRef(new Map());
    const currentQueryHashRef = useRef(queryHash);
    currentQueryHashRef.current = queryHash;
    // Read the latest cached envelope reactively so `hasNextPage` flips
    // when fetchNextPage lands a page whose nextCursor is null.
    const cached = queryClient.getQueryData(queryKey);
    const relationshipCached = relationship?.collection === undefined ? cached : materializeViewerRelationshipData(queryClient, queryKey, relationshipScope, relationship, viewerArgs, cached);
    const hasNextPage = (cached?.nextCursor ?? null) !== null;
    const isFetchingNextPage = inFlightByQueryRef.current.has(queryHash);
    const currentFailure = failuresByQueryRef.current.get(queryHash);
    const nextPageError = currentFailure !== undefined && currentFailure.cursor === cached?.nextCursor ? currentFailure.error : null;
    // Snapshot of the cached rows — exposed as `rows` for parents that need
    // the whole array (carousels, ranked widgets). The page-level `select`
    // nulls `.data`, so this cache read is the only parent-side row access.
    const materializeRows = current => {
      const materialized = relationship?.collection === undefined ? current : materializeViewerRelationshipData(queryClient, queryKey, relationshipScope, relationship, viewerArgs, current);
      const cachedData = materialized?.data ?? EMPTY_ROWS;
      return isNormalized && entityQueryKey !== undefined && (cachedData.length === 0 || typeof cachedData[0] === 'string') ? readEntityRows(queryClient, entityQueryKey, cachedData, opts.spec.viewerOverlay, viewerArgs, viewerScope, opts.spec.entityProjection, opts.spec.nestedEntityProjection, current?._indexFields, networkScope) : cachedData;
    };
    const selectRows = () => materializeRows(queryClient.getQueryData(queryKey));
    const trackRows = useTrackedQueryValue(queryClient, queryHash, selectRows, isNormalized);
    // A duplicate-only REST page advances progress without changing rows. Track
    // the receipt separately so sibling readers update while Items stay isolated.
    const trackReceipt = useTrackedQueryValue(queryClient, queryHash, () => queryClient.getQueryData(queryKey)?._restPageLastTimestamp, false);
    const previousRows = resolvedRowsRef.current;
    // Keep the render's index, including an unread previous filter, without
    // reconstructing entities for consumers that only use IDs or pagination.
    const readRows = relationshipCached === undefined && q.isPlaceholderData && previousRows !== null && previousRows.queryHash !== queryHash ? previousRows.readRows : createLazyRowsReader(() => materializeRows(cached));
    if (relationshipCached !== undefined) {
      resolvedRowsRef.current = {
        queryHash,
        readRows
      };
    }
    // `fetchNextPage` reads the latest cursor + args directly from the
    // cache/closure on every invocation, so it does NOT need to be
    // useCallback-stable to behave correctly. We accept a fresh function
    // identity per render — the call-site uses are `onClick`, `onVisible`
    // (routed via the ref pattern below), and the public hook result
    // surface, none of which care about identity. Consumers wiring it
    // into a `useEffect` dep array would re-run each render; that's an
    // unusual pattern for an imperative fetcher and is the caller's
    // choice to stabilise via `useCallback` themselves if needed.
    const appendPage = (capturedQueryKey, capturedQueryHash, capturedArgs, cursor) => {
      const pagination = opts.pagination;
      if (pagination === undefined) return Promise.resolve();
      const existing = inFlightByQueryRef.current.get(capturedQueryHash);
      if (existing !== undefined) return existing.promise;
      const generation = queryClient.getQueryState(capturedQueryKey)?.dataUpdateCount;
      const request = (async () => {
        try {
          const observedAtMs = Date.now();
          const response = await pagination.fetchPage(capturedArgs, cursor);
          const next = filterTerminalRows(queryClient, capturedQueryKey, {
            ...response,
            _restPageLastTimestamp: lastPageTimestamp(response.data)
          });
          // A refresh or another page may have replaced this cursor while the request ran.
          // Check before entity hydration so stale rows cannot overwrite newer snapshots.
          const currentPage = queryClient.getQueryData(capturedQueryKey);
          if (callOpts?.resetPaginationOnRefetch === true && (currentQueryHashRef.current !== capturedQueryHash || currentPage?.nextCursor !== cursor || queryClient.getQueryState(capturedQueryKey)?.dataUpdateCount !== generation)) return;
          if (callOpts?.resetPaginationOnRefetch === true && currentPage?.rankingRevision !== next.rankingRevision) {
            await refetchRealtimeQuery(queryClient, capturedQueryKey);
            return;
          }
          if (relationship?.collection !== undefined) {
            writeViewerRelationshipCollection(queryClient, capturedQueryKey, viewerScope, relationship, capturedArgs, next, viewerAccountId, {
              source: 'rest',
              observedAtMs
            }, 'append');
          }
          if (isNormalized && entityQueryKey !== undefined) {
            const page = splitRowsIntoEntities(queryClient, next, entityQueryKey, rowKey, {
              source: 'rest',
              observedAtMs
            }, opts.spec.viewerOverlay, viewerArgs, viewerScope, opts.spec.entityProjection, opts.spec.nestedEntityProjection, opts.spec.entityGcTimeMs, capturedQueryKey);
            if (page !== undefined) {
              queryClient.setQueryData(capturedQueryKey, prev => appendNormalizedPage(prev, page));
            }
            failuresByQueryRef.current.delete(capturedQueryHash);
            return;
          }
          queryClient.setQueryData(capturedQueryKey, prev => {
            const prevRows = prev?.data ?? [];
            // Dedupe by `rowKey` — a backend cursor implementation that
            // returns the boundary row in two adjacent pages would otherwise
            // produce duplicate React keys + double-counted rows. We trust
            // the EARLIER page's copy (it's been receiving realtime patches);
            // the new page's duplicates are dropped.
            const seen = new Set();
            for (const row of prevRows) seen.add(String(row[rowKey]));
            const appended = [];
            for (const row of next.data) {
              const key = String(row[rowKey]);
              if (seen.has(key)) continue;
              seen.add(key);
              appended.push(row);
            }
            // Carry the active sort context forward — `fetchNextPage`
            // targets the SAME query (same args, same sort), so the
            // realtime reducer must keep seeing `_sort` to live re-rank
            // the now-larger loaded set. Prefer the freshly-fetched page's
            // `_sort` (identical), fall back to the prior cache.
            const carriedSort = next._sort ?? prev?._sort;
            const mergedRows = pagination.mergeMode === 'prepend' ? [...appended, ...prevRows] : [...prevRows, ...appended];
            const merged = {
              ...prev,
              ...next,
              data: mergedRows,
              nextCursor: next.nextCursor,
              hasMore: next.hasMore,
              ...(carriedSort !== undefined ? {
                _sort: carriedSort
              } : {})
            };
            return merged;
          });
          failuresByQueryRef.current.delete(capturedQueryHash);
        } catch (error) {
          if (callOpts?.resetPaginationOnRefetch === true && currentQueryHashRef.current === capturedQueryHash && typeof error === 'object' && error !== null && 'apiCode' in error && error.apiCode === 'SYSTEM_INVALID_CURSOR') {
            const outcome = await refetchRealtimeQuery(queryClient, capturedQueryKey);
            if (outcome === 'success') {
              failuresByQueryRef.current.delete(capturedQueryHash);
              return;
            }
          }
          failuresByQueryRef.current.set(capturedQueryHash, {
            queryHash: capturedQueryHash,
            cursor,
            error
          });
          if (currentQueryHashRef.current === capturedQueryHash) throw error;
        } finally {
          inFlightByQueryRef.current.delete(capturedQueryHash);
          if (currentQueryHashRef.current === capturedQueryHash) {
            setPaginationRevision(revision => revision + 1);
          }
        }
      })();
      inFlightByQueryRef.current.set(capturedQueryHash, {
        cursor,
        promise: request
      });
      setPaginationRevision(revision => revision + 1);
      return request;
    };
    const fetchNextPage = () => {
      if (opts.pagination === undefined) return Promise.resolve();
      const current = queryClient.getQueryData(queryKey);
      const cursor = current?.nextCursor ?? null;
      if (cursor === null) return Promise.resolve();
      const failure = failuresByQueryRef.current.get(queryHash);
      if (failure?.cursor === cursor) return Promise.resolve();
      if (failure !== undefined) failuresByQueryRef.current.delete(queryHash);
      return appendPage(queryKey, queryHash, args, cursor);
    };
    const retryNextPage = () => {
      if (opts.pagination === undefined) return Promise.resolve();
      const failure = failuresByQueryRef.current.get(queryHash);
      if (failure === undefined) return Promise.resolve();
      const current = queryClient.getQueryData(queryKey);
      if (current?.nextCursor !== failure.cursor) return Promise.resolve();
      return appendPage(queryKey, queryHash, args, failure.cursor);
    };
    // `Items` captured `triggerNextPage` at construction (one-shot ref
    // memo above). Storing the latest fetchNextPage on a ref means the
    // sentinel's closure always invokes the freshest fetcher, while the
    // Items component reference stays stable across renders.
    //
    // The ref write happens in `useEffect` (post-commit) — NOT during
    // render. Writing to a ref during render is the kind of escape-hatch
    // pattern React Compiler refuses to compile and that masks bugs (the
    // ref carries the in-progress-render closure rather than the
    // committed one).
    const triggerNextPageRef = useRef(() => {});
    useEffect(() => {
      triggerNextPageRef.current = () => {
        void fetchNextPage().catch(() => undefined);
      };
    });
    function triggerNextPage() {
      triggerNextPageRef.current();
    }
    // CRITICAL — do NOT spread `q`. TanStack v5 wraps `useQuery`'s return
    // value in a tracked-properties Proxy: reading `q.data` marks `data` as
    // "tracked" so the Proxy notifies the consumer on data changes. A spread
    // iterates ALL enumerable properties, marking EVERY field as tracked, so
    // every cache patch would trigger a parent re-render even when the
    // consumer reads only `Items` / `isPending` / `isError`.
    //
    // Instead, return a Proxy wrapper: SDK-added fields (`itemObserver`,
    // `Items`) are served from a local bag; everything else is delegated to
    // `q`, preserving TanStack's lazy field tracking. The consumer then
    // re-renders only when it actually reads a changed field.
    return createListResultProxy(q, itemObserver, Items, ids, readRows, trackRows, trackReceipt, queryKey, rowKey, isFetchingNextPage, hasNextPage, fetchNextPage, nextPageError, retryNextPage, queryClient, isNormalized && entityQueryKey !== undefined ? entityQueryKey : undefined, opts.spec.viewerOverlay, opts.spec.viewerOverlay === undefined ? undefined : viewerArgs, opts.spec.viewerOverlay === undefined ? undefined : viewerScope, opts.spec.entityProjection, entityRowProjector, opts.spec.nestedEntityProjection);
  };
}
function createLazyRowsReader(selectRows) {
  let snapshot;
  return () => {
    snapshot ??= selectRows();
    return snapshot;
  };
}
function createListResultProxy(q, itemObserver, Items, ids, readRows, trackRows, trackReceipt, queryKey, rowKey, isFetchingNextPage, hasNextPage, fetchNextPage, nextPageError, retryNextPage, queryClient, entityQueryKey, viewerOverlay, viewerArgs, viewerScope, entityProjection, entityRowProjector, nestedEntityProjection) {
  // Plain object with getters that delegate every TanStack-result field
  // access to `q` (TanStack's tracked-properties Proxy). Only the fields
  // the consumer actually reads via destructure get tracked — same
  // contract as their Proxy, but simpler / no ownKeys/getOwnPropertyDescriptor
  // edge cases that may inadvertently trigger Proxy-internal property
  // enumeration during React/devtools introspection.
  //
  // `liveSinceMountIds` is computed lazily via a getter that consults the
  // module-level `live-since-mount-store` projection cache — same identity-
  // stability contract as `ids` (the cache key includes the `ids` reference).
  const queryHash = hashKey(queryKey);
  const result = {
    itemObserver,
    Items,
    ids,
    queryKey,
    rowKey,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    nextPageError,
    retryNextPage,
    ...(entityQueryKey !== undefined ? {
      entityQueryKey
    } : {}),
    ...(entityProjection !== undefined ? {
      entityProjection
    } : {}),
    ...(nestedEntityProjection !== undefined ? {
      nestedEntityProjection
    } : {}),
    ...(entityRowProjector !== undefined ? {
      projectEntityRow: entityRowProjector
    } : {}),
    ...(viewerOverlay !== undefined ? {
      viewerOverlay,
      viewerArgs,
      viewerScope
    } : {}),
    get liveSinceMountIds() {
      const fence = readFenceFromResult(q);
      if (fence === null) return EMPTY_LIVE_IDS;
      return projectLiveSinceMountIds(queryClient, queryHash, fence, ids);
    }
  };
  // Prop inspection must not opt an IDs-only owner into whole-row updates.
  // Explicit access/destructuring still activates the subscription.
  Object.defineProperty(result, 'rows', {
    enumerable: false,
    get() {
      trackRows();
      return readRows();
    }
  });
  Object.defineProperty(result, 'lastFetchedTimestamp', {
    enumerable: false,
    get() {
      trackReceipt();
      return queryClient.getQueryData(queryKey)?._restPageLastTimestamp;
    }
  });
  const delegateKeys = ['data', 'error', 'isPending', 'isError', 'isLoading', 'isFetching', 'isSuccess', 'isStale', 'isPlaceholderData', 'isInitialLoading', 'isRefetching', 'isLoadingError', 'isRefetchError', 'isPaused', 'isFetched', 'isFetchedAfterMount', 'isEnabled', 'status', 'fetchStatus', 'dataUpdatedAt', 'errorUpdatedAt', 'failureCount', 'failureReason', 'refetch', 'promise', 'ref'];
  for (const k of delegateKeys) {
    // `enumerable: false` so DevTools / Object.keys / spread can't sweep
    // through the getters and accidentally trigger TanStack's tracked-
    // properties Proxy on `q` for fields the consumer never asked for.
    // Destructuring (`const { isPending } = result`) still invokes the
    // getter — enumerable only affects iteration, not explicit access.
    Object.defineProperty(result, k, {
      enumerable: false,
      configurable: true,
      get() {
        return q[k];
      }
    });
  }
  return result;
}
/** True while a normalized feed still holds full rows (pre-heal), not ids. */
function holdsUnsplitRows(queryClient, queryKey) {
  const rows = queryClient.getQueryData(queryKey)?.data;
  return Array.isArray(rows) && rows.length > 0 && typeof rows[0] !== 'string';
}
function lastPageTimestamp(rows) {
  const row = rows.at(-1);
  return row !== null && typeof row === 'object' && 'timestamp' in row && typeof row.timestamp === 'string' ? row.timestamp : undefined;
}